/**
 * The view ⟷ URL mapping, and nothing else.
 *
 * This app has no router and does not need one: navigation is a `view` string,
 * every view is already declared in views.js, and api/src/server.js answers
 * index.html for any non-/api/ path, so a deep link resolves on a hard refresh
 * without a single server change.
 *
 * What this module buys is the browser's back button. Before it, the drill-down
 * argument lived in React state, which meant a drilled-down ledger could be
 * reached but never linked to and never gone back from.
 *
 * IN `lib/` FOR THE SAME REASON views.js IS: `npm test -w web` is node's own
 * runner over `src/lib/*.test.js`, and node cannot parse JSX. A route table
 * that only a component could exercise is a route table nobody checks.
 */
import { VIEW_CAPABILITY } from './views.js';

export const DEFAULT_VIEW = 'dashboard';

/** The view for a pathname, or null when the path names no known view. */
export function viewForPath(pathname) {
  const slug = String(pathname ?? '').replace(/^\/+|\/+$/g, '');
  if (slug === '') return DEFAULT_VIEW;
  if (codeIdForPath(pathname)) return 'code';
  // Object.hasOwn, NOT `VIEW_CAPABILITY[slug] !== undefined` — a plain object
  // reaches Object.prototype, so "/toString" would otherwise find a function
  // and be treated as a view. Same guard, and same reason, as canView.
  return Object.hasOwn(VIEW_CAPABILITY, slug) ? slug : null;
}

const CODE_PATH = /^\/?code\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i;

/** The request id in /code/<id>, or null. Only a well-formed uuid counts. */
export function codeIdForPath(pathname) {
  return CODE_PATH.exec(String(pathname ?? ''))?.[1]?.toLowerCase() ?? null;
}

/** The pathname for a view. The dashboard is the root, not "/dashboard". */
export function pathForView(view, arg = null) {
  if (view === 'code' && arg?.codeId) return `/code/${arg.codeId}`;
  return view === DEFAULT_VIEW ? '/' : `/${view}`;
}

/**
 * Read a route off the address bar.
 *
 * The company is in the query rather than the path because it is orthogonal to
 * the screen: every report means "this screen, for that company", and putting
 * it in the path would make each one two routes to keep in step.
 */
export function parseRoute(pathname, search) {
  const q = new URLSearchParams(search ?? '');
  const accountId = q.get('account');
  const codeId = codeIdForPath(pathname);
  return {
    view: viewForPath(pathname),
    entityId: q.get('company'),
    arg: codeId ? { codeId } : accountId
      ? { accountId, from: q.get('from'), to: q.get('to') }
      : null,
  };
}

/** The address bar for a route. The inverse of parseRoute. */
export function routeUrl({ view, entityId, arg }) {
  const q = new URLSearchParams();
  if (entityId) q.set('company', entityId);
  if (arg?.accountId && view !== 'code') {
    q.set('account', arg.accountId);
    if (arg.from) q.set('from', arg.from);
    if (arg.to) q.set('to', arg.to);
  }
  const query = q.toString();
  const path = pathForView(view, arg);
  return query ? `${path}?${query}` : path;
}
